import React from 'react';
import { formatDublinDateTime } from '../../utils/date';
import { Link } from 'react-router-dom';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useListStudentIncidentsQuery } from '../../store/api/localApi';
import { StaffIncidentStatus } from '../../types';
import IncidentSeverityChip from './IncidentSeverityChip';

interface Props {
  studentId: string;
  /** Resolved by the caller (e.g. urlPrefix from useLocationContext) so the
   *  incident-detail links route under the viewer's correct scope — same
   *  convention as ObservationsAuditSection's detailHrefBase. */
  detailHrefBase: string;
}

function formatDateTime(iso: string): string {
  return formatDublinDateTime(iso);
}

/**
 * Staff/admin audit view of every incident recorded for one student, on the
 * student's own profile page. Uses `GET /students/{studentId}/incidents`
 * (`useListStudentIncidentsQuery`) — the STAFF per-child history, full
 * visibility including DRAFT/WITHDRAWN — NOT the parent-facing list.
 * Parents never reach this: the page that hosts this section is staff/admin
 * only (StudentDetail.tsx gates the whole "Registration snapshot" block on
 * `!isParent`).
 */
const IncidentHistorySection: React.FC<Props> = ({ studentId, detailHrefBase }) => {
  const { data, isLoading, error } = useListStudentIncidentsQuery({ studentId });
  const incidents = data?.incidents ?? [];

  return (
    <div className="mt-8">
      <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center gap-2">
        <ExclamationTriangleIcon className="h-5 w-5 text-gray-400" />
        Incident reports
      </h3>
      <div
        data-testid="student-detail-incidents-list"
        className="mt-3 bg-white rounded-lg shadow divide-y divide-gray-100 overflow-hidden"
      >
        {isLoading && <p className="px-4 py-6 text-sm text-gray-500 text-center">Loading…</p>}
        {error && !isLoading && (
          <p className="px-4 py-6 text-sm text-red-500 text-center">Failed to load incidents.</p>
        )}
        {!isLoading && !error && incidents.length === 0 && (
          <p className="px-4 py-6 text-sm text-gray-500 text-center">No incidents recorded.</p>
        )}
        {incidents.map((incident) => {
          const isWithdrawn = incident.status === StaffIncidentStatus.WITHDRAWN;
          return (
            <Link
              key={incident.id}
              to={`${detailHrefBase}/incidents/${incident.id}`}
              data-testid={`student-detail-incident-${incident.id}`}
              className={`block px-4 py-3 hover:bg-gray-50 ${isWithdrawn ? 'opacity-60' : ''}`}
            >
              <div className="flex justify-between items-baseline gap-2">
                <span className="font-medium text-sm text-gray-900">
                  {formatDateTime(incident.occurredAt)}
                </span>
                <IncidentSeverityChip severity={incident.severity} />
              </div>
              <p className="text-sm text-gray-700 mt-1 line-clamp-2">{incident.description}</p>
              {isWithdrawn && (
                <span className="mt-1 inline-block text-xs font-medium text-gray-600 bg-gray-200 px-1.5 py-0.5 rounded">
                  Withdrawn
                </span>
              )}
            </Link>
          );
        })}
      </div>
    </div>
  );
};

export default IncidentHistorySection;
